import { ActionIcon, Box, Flex, Skeleton, Tooltip } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons";
import { useContext, useMemo } from "react";
import { getNestedFieldValue } from "../../../ideaNiv/helpers/calculateImpactResults";
import { OverflownText } from "../../../OverflownText";
import { buildIdeaUrlWithEvidenceFilters } from "../../../../utils/ideasUrlState";
import FiltersContext from "../../filters/context/FiltersContext";

function formatDisplayText(val: unknown): string {
  if (val == null) return "";
  if (typeof val === "string" || typeof val === "number") return String(val);
  if (typeof val === "object") {
    const o = val as Record<string, unknown>;
    if (typeof o.title === "string") return o.title;
    if (typeof o.name === "string") return o.name;
  }
  return String(val);
}

export const TitleLinkCell: React.FC<any> = ({ data, col, renderParams }) => {
  const { activeFilters } = useContext(FiltersContext);

  const resolved = getNestedFieldValue(data, col.accessor);
  const rawValue =
    resolved !== undefined && resolved !== null ? resolved : renderParams?.default;
  const displayText = useMemo(
    () => formatDisplayText(rawValue),
    [rawValue],
  );

  const parentIdeaIdRaw =
    data?.parentIdeaId ?? (data?.parentIdea as { _id?: string } | undefined)?._id;
  const parentIdeaId =
    typeof parentIdeaIdRaw === "string" && parentIdeaIdRaw.trim()
      ? parentIdeaIdRaw.trim()
      : undefined;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        textOverflow: "ellipsis",
        overflow: "hidden",
        position: "relative",
        cursor: parentIdeaId ? "pointer" : "default",
      }}
      className={`title-link-cell-${col.accessor.replace(/\./g, "-")}`}
    >
      <OverflownText
        sx={{
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
          maxWidth: "99%",
          color:  "#212529",
          fontWeight: 500,
          "&:hover": { color: "#4A4AD4" },
        }}
        overTooltip={
          `${displayText || "Parent idea"}`
        }
      >
        {displayText}
      </OverflownText>

      {parentIdeaId && (
        <Tooltip label="Open parent idea in new tab" withArrow withinPortal>
          <ActionIcon
            className={`external-link-icon external-link-${col.accessor.replace(/\./g, "-")}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(
                buildIdeaUrlWithEvidenceFilters(
                  parentIdeaId,
                  window.location.search,
                  activeFilters,
                ),
                "_blank",
              );
            }}
            variant="transparent"
            size="sm"
            sx={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              right: "0px",
              zIndex: 10,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(2px)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
              },
              "&:active": {
                transform: "translateY(-50%)",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
              },
              "&:focus": {
                transform: "translateY(-50%)",
              },
            }}
          >
            <IconExternalLink size={18} color="#5b60e3" />
          </ActionIcon>
        </Tooltip>
      )}
    </Box>
  );
};

export const TitleLinkCellSkeleton: React.FC<{ col: any; id?: string }> = ({
  col,
  id,
}) => {
  const renderParams = col.renderParams || {};
  const maxWidth = renderParams.maxWidth || col.maxWidth || "350px";
  const columnWidth = col.width;
  const minWidth = col.minWidth;

  const skeletonWidth = useMemo(() => {
    const seedString = id || col.accessor;
    const hash = seedString.split("").reduce((acc: number, char: string) => {
      return acc + char.charCodeAt(0);
    }, 0);
    const percentage = 40 + (hash % 51);
    return `${percentage}%`;
  }, [col.accessor, id]);

  return (
    <Box
      sx={{
        maxWidth,
        width: columnWidth || "100%",
        minWidth,
        position: "relative",
        paddingRight: "23px",
      }}
    >
      <Flex align="center">
        <Skeleton height={16} width={skeletonWidth} radius="sm" />
      </Flex>
    </Box>
  );
};
